import React, { useMemo, useCallback, ReactNode } from 'react';
import { Calendar, Plus, BirdIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getDisplayTodayDate } from '../utils';
import { MoldOrder, UserPreference } from '../../types/mold';
import { useAppState } from '../../context/AppStateContext';
import { usePreferences } from '../../context/PreferencesContext';
import MainContainer from '../layout/MainContainer';
import { TopbarBackgroundStyle } from '../layout/EnhancedModernTopbar';
import Board from './Board';
import BoardTypeDropdown from './BoardTypeDropdown';
import { BoardConfig } from '@/models/appState';

interface NewKanbanBoardProps {
    moldOrders: MoldOrder[];
    preferences?: UserPreference | null;
    boardType?: string;
    displayMode?: 'tv' | 'default';
}

const NewKanbanBoard: React.FC<NewKanbanBoardProps> = ({
    moldOrders,
    preferences,
    boardType = 'maintenance',
    displayMode = 'default',
}) => {
    const navigate = useNavigate();
    const { updatePreference } = usePreferences();
    const {
        getBoards,
    } = useAppState();
    const today = getDisplayTodayDate();
    const boards = getBoards();
    const currentBoard = useMemo(() => {
        return boards.find(group => group.key === boardType);
    }, [boardType, boards]);
    const relatedBoard = useMemo(() => {
        if(currentBoard && currentBoard.relatedBoardKey) {
            return boards.find(board => board.key === currentBoard?.relatedBoardKey);
        }
        return undefined;
    }, [currentBoard, boards]);
    const colorCode = currentBoard?.uiConfig?.colorCode || 'blue';
    const isBoardEnabled = currentBoard?.isEnable ?? false;

    const topbarBackgroundStyle = useMemo(() => {
        switch (colorCode) {
            case 'blue':
                return TopbarBackgroundStyle.BLUE;
            case 'yellow':
                return TopbarBackgroundStyle.YELLOW;
            case 'green':
                return TopbarBackgroundStyle.GREEN;
            case 'red':
                return TopbarBackgroundStyle.RED;
            default:
                return TopbarBackgroundStyle.DEFAULT;
        }
    }, [colorCode]);

    const handleBoardTypeChange = useCallback((type: string) => {
        navigate(`?boardType=${type}`);
        updatePreference({ kanbanBoardType: type, selectedOrderType: 'ALL' });
    }, [navigate, updatePreference]);

    const handleCreateNewTask = useCallback(() => {
        if (currentBoard && currentBoard.createNewOrderUrl) {
            navigate(currentBoard.createNewOrderUrl);
        }
    }, [currentBoard, navigate]);

    const BoardHeader: ReactNode = (<div className="flex flex-row sm:items-center gap-2 w-full sm:w-auto">
        <div className="hidden sm:flex items-center">
            <h1 className={`ml-0 text-base sm:text-2xl font-bold text-${colorCode}-800 truncate`}>
                สถานะใบงาน{currentBoard?.title}
            </h1>
        </div>

        {/* Board Type Switcher - Compact Dropdown */}
        <div className='flex flex-row gap-1 items-center'>
            <BoardTypeDropdown
                boards={boards}
                currentBoard={currentBoard}
                boardType={boardType}
                colorCode={colorCode}
                onBoardTypeChange={handleBoardTypeChange}
            />

            {displayMode === 'default' && currentBoard?.createNewOrderUrl && (<button
                onClick={handleCreateNewTask}
                className="bg-blue-600 h-full text-sm shadow-md text-white px-3 py-1.5 rounded-full hidden sm:flex items-center hover:bg-blue-700 transition duration-300"
            >
                <Plus className="h-auto w-4" />
                <p className="hidden md:flex md:ml-1">สร้างใบงาน</p>
            </button>)}
        </div>
        <div className="hidden sm:flex items-center h-full">
            <Calendar size={16} className="text-gray-600" />
            <span className="hidden xl:block ml-1.5 text-sm text-gray-700">{today}</span>
            <span className="block xl:hidden ml-1.5 text-sm text-gray-700">{today}</span>
        </div>
    </div>)

    const filterByBoardGroup = (order: MoldOrder, currentBoard: BoardConfig | undefined) => {
        return currentBoard && currentBoard?.moldOrderTypes.includes(order.type);
    }

    const filterByOrderVersion = (order: MoldOrder, version: number | undefined) => {
        if (version === undefined || version === null) return true;
        return order.orderWorkflowDefinition?.version && (order.orderWorkflowDefinition?.version === version)
    }

    const filteredOrders = useMemo(() => {
        return moldOrders
            .filter(order => filterByBoardGroup(order, currentBoard))
            .filter(order => filterByOrderVersion(order, currentBoard?.version));
    }, [currentBoard, moldOrders]);

    const relatedBoardOrders = useMemo(() => {
        if (!relatedBoard) return [];
        return moldOrders
            .filter(order => filterByBoardGroup(order, relatedBoard))
            .filter(order => filterByOrderVersion(order, relatedBoard?.version));
    }, [relatedBoard, moldOrders]);

    return (
        <MainContainer
            showSidebar={displayMode === 'default'}
            customHeader={BoardHeader}
            enableUserLogoutSection={displayMode === 'default'}
            enableWideScreen={displayMode === 'default'}
            topbarBackgroundStyle={topbarBackgroundStyle}>
            <div className="flex flex-col bg-gray min-h-screen max-h-screen mt-2">
                {isBoardEnabled && currentBoard ?
                    <Board
                        moldOrders={filteredOrders}
                        preferences={preferences}
                        boardType={boardType}
                        currentBoard={currentBoard}
                        relatedBoard={relatedBoard}
                        relatedBoardOrdersCount={relatedBoardOrders.length}
                        showFilterBar={displayMode === 'default'}
                        displayMode={displayMode}
                    /> :
                    (<div className="flex flex-col items-center justify-center flex-grow text-center text-gray-500">
                        <BirdIcon size={48} className="mb-4 text-gray-400 animate-bounce" />
                        <h2 className="text-2xl font-semibold text-gray-700">เร็วๆ นี้</h2>
                        <p className="text-gray-600 mt-2">ฟีเจอร์นี้กำลังอยู่ในระหว่างการพัฒนา</p>
                    </div>)
                }
            </div>
        </MainContainer>
    );
};

export default NewKanbanBoard;
